import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { signinRequest } from 'slices/signinRequest';
import { hostManagesSession } from 'utils/hostSession';
import { getNavBlobId } from 'utils/navigationState';
import { wipeOutTracesDeep } from 'utils/storage';
import {
  collectExternalOrigins,
  exitExternalAppIframes,
  hideDocumentForTeardown,
  PANIC_IFRAME_EXIT_CAP_MS,
  requestExternalOriginWipes,
  SIGNOUT_IFRAME_EXIT_CAP_MS,
  signalIframesCleanup,
  wipeDocumentAndReload,
} from 'utils/teardown';

const initialState = {
  encryptionKey: null,
  status: null,
  token: null,
  user: null,
  navigationState: null,
};

// Thin RTK wrapper around the shared signin flow (slices/signinRequest). The login bundle calls
// signinRequest directly without Redux Toolkit; the main app keeps this thunk for its store.
export const signin = createAsyncThunk('auth/signin', ([authAPI, credentials]) =>
  signinRequest(authAPI, credentials)
);

// Shared tail of both signout thunks. Our own origin and the embedded ones are wiped side by side
// — a slow foreign app must not delay wiping what is ours — and only when both are done (or the
// cap runs out) is the document destroyed and the tab sent away. A panic is on the shorter leash
// and lands on the safe page instead of the login screen.
const teardownAndReload = async ({ panic }) => {
  await Promise.all([
    wipeOutTracesDeep(),
    exitExternalAppIframes(panic ? PANIC_IFRAME_EXIT_CAP_MS : SIGNOUT_IFRAME_EXIT_CAP_MS, {
      panic,
    }),
  ]);
  wipeDocumentAndReload({ safePage: panic });
};

// Everything that must happen the instant a teardown starts, in order: the screen goes blank, then
// the embedded apps are told to wipe themselves both in-page and over the network (the latter
// survives even if this tab dies here).
const startTeardown = () => {
  hideDocumentForTeardown();
  window.navigator?.vibrate?.(100);
  signalIframesCleanup();
  requestExternalOriginWipes(collectExternalOrigins());
};

// A panic can fire before sign-in (the detectors are armed for the whole life of the tab), and then
// there is no session to revoke — the calls would only be rejected. Skip them and spend the time on
// the wipe instead.
const hasSession = (getState) => !!getState?.()?.auth?.token;

export const hardSignout = createAsyncThunk('auth/hardSignout', async (arg, { getState }) => {
  // Unconditional inside the auth iframe (a panic must always tear down) — but never under a native
  // host (window.In6to.hosted), which owns teardown itself and shares this token with its own
  // session; revoking and unlinking here would kill the host's session. See utils/hostSession.
  if (window.In6to.hosted) return;
  const [authAPI, blobsAPI, { panic = false } = {}] = arg;

  startTeardown();

  if (hasSession(getState)) {
    // Deliberate hard logout (device unlink): drop this device's saved navigation state so the
    // next login starts fresh (soft signout keeps it for the 5-min restore window). Fire-and-forget.
    const navBlobId = getNavBlobId();
    if (navBlobId) blobsAPI.destroy({ id: navBlobId });

    authAPI.destroy({ unlink: true }); // Uses keepalive to ensure request completes
  }

  await teardownAndReload({ panic });
});

export const signout = createAsyncThunk('auth/signout', async (arg, { getState }) => {
  if (hostManagesSession()) return;

  const [authAPI, { panic = false } = {}] = Array.isArray(arg) ? arg : [arg];

  startTeardown();

  if (hasSession(getState)) authAPI.destroy(); // Uses keepalive to ensure request completes

  await teardownAndReload({ panic });
});

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Pure reducer: applies notification settings. The API call lives in the updateAuth thunk.
    authUpdated: (state, action) => {
      const { firebase_token, notify } = action.payload;
      state.user.firebase_token = firebase_token;
      state.user.notify = notify;
    },
    updateTokenAfterReauth: (state, action) => {
      const { token, user, encryptionKey, navigationState } = action.payload;
      state.token = token;
      state.user = user;
      state.encryptionKey = encryptionKey;
      state.navigationState = navigationState ?? null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hardSignout.pending, (state) => {
        state.encryptionKey = null;
        state.status = null;
        state.token = null;
        state.user = null;
        state.navigationState = null;
      })
      .addCase(signout.pending, (state) => {
        state.encryptionKey = null;
        state.status = null;
        state.token = null;
        state.user = null;
        state.navigationState = null;
      })
      .addCase(signin.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(signin.rejected, (state) => {
        state.status = 'rejected';
      })
      .addCase(signin.fulfilled, (state, { payload }) => {
        if (payload.error) {
          state.status = 'rejected';
          return;
        }
        state.encryptionKey = payload.encryptionKey;
        state.token = payload.user.jwt;
        state.user = payload.user;
        state.navigationState = payload.navigationState ?? null;
        state.status = 'ready';
      });
  },
});

export const { authUpdated, updateTokenAfterReauth } = authSlice.actions;

// Thunk keeps the API call out of the reducer. The call signature is unchanged —
// dispatch(updateAuth([AuthAPI, params])) — so existing call-sites keep working.
export const updateAuth =
  ([AuthAPI, params]) =>
  (dispatch) => {
    AuthAPI.update(params);
    dispatch(authUpdated(params));
  };

export const selectEncryptionKey = (state) => state.auth.encryptionKey;
export const selectToken = (state) => state.auth.token;
export const selectUser = (state) => state.auth.user;
export const selectNavigationState = (state) => state.auth.navigationState;

export default authSlice.reducer;
